import { useEffect } from 'react';
import { openModal } from '@kcd/app-interface/dist/web2app';
import { trackEvent } from '@kcd/event-tracker';

import {
  ExpectedBonuspackDiscountNoticeTooltip,
  ExpectedMembershipDiscountNoticeTooltip,
  MembershipDiscountNoticeTooltip,
} from '~/domains/plusMembership/components/Tooltips';
import { useMembershipStatus } from '~/domains/plusMembership/hooks/useMembershipStatus';
import { useProductDetailsNudge } from '~/domains/plusMembership/queries';
import type { OAISchema } from '~/types/openapi';

import { MembershipPromotionCountDown } from './MembershipPromotionCountdown';
import { MembershipTrialCountdown } from './MembershipTrialCountdown';

import {
  BonuspackCloverFill18x18Icon,
  MembershipStandard28x14Icon,
  ThreadIcon16x16,
} from '~/assets/icons';

type Props = {
  data: OAISchema['MarketProductDetailsResponseV2'];
};

// 스탠다드 가입 가능 유저만 테스트 - 종료후 코드 정리
export function MembershipTrialPrice({ data }: Props) {
  const { data: nudgeData } = useProductDetailsNudge(data.offerId!);
  const {
    getRegisterMembershipUrl,
    isTrialAvailable,
    isAvailableToSubscribe,
    isAvailableToSubscribeBonusPack,
  } = useMembershipStatus();

  const nudgeType = (() => {
    if (isTrialAvailable) {
      return 'plus_membership_free';
    }

    if (isAvailableToSubscribe) {
      return 'plus_membership';
    }

    if (isAvailableToSubscribeBonusPack) {
      return 'lucky_bonus_pack';
    }

    return '';
  })();

  useEffect(() => {
    trackEvent('viewed banner in 마켓/상품상세/멤버십넛지', {
      clicked_area: 'membership_coupon_price',
      membership_nudge_type: nudgeType,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClickSubscribe = () => {
    const registerUrl = getRegisterMembershipUrl();

    trackEvent('clicked button in 마켓/상품상세/멤버십넛지', {
      clicked_area: 'membership_coupon_price',
      membership_nudge_type: nudgeType,
    });

    if (registerUrl) {
      openModal({
        url: registerUrl,
      });
    }
  };

  const showNudgeSection =
    nudgeData &&
    !!nudgeData.details.length &&
    // 넛지 할인 가격이 현재 가격보다 낮으면 노출
    nudgeData?.details.some(
      (detail) => (detail.nudgeAmount ?? 0) < (data.totalDiscountedPrice ?? 0),
    );

  return (
    <div className="relative flex flex-col gap-8">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-4 typo-heading200">
          <p className="typo-paragraph100-semibold">나의할인가</p>
          <MembershipDiscountNoticeTooltip />
        </div>
        <p className="flex items-center gap-8">
          <strong className="typo-heading200-bold">
            {data.totalDiscountedPrice?.toLocaleString()}원
          </strong>
          <span className="text-hint typo-paragraph200-regular">
            {data.price ? `${data.price.toLocaleString()}원` : '상담신청 상품'}
          </span>
        </p>
        <div className="flex flex-col">
          {data.discountResult.items.map((result) => {
            return (
              <p
                className="flex items-center gap-4 break-all text-paragraph typo-paragraph100-regular"
                key={result.description}
              >
                <ThreadIcon16x16 className="shrink-0 grow-0 basis-16" />
                {result.description}
              </p>
            );
          })}
        </div>
        {/* 보너스팩 가입 대상자만 노출 */}
        {isAvailableToSubscribeBonusPack && <MembershipPromotionCountDown />}
      </div>

      {showNudgeSection && (
        <>
          {/* <ContentDivider className="!mx-0" /> */}
          <div className="flex justify-between rounded-16 border border-solid border-danger-regular bg-gradient-to-b from-[#FFEFEE] to-gray-000 px-20 py-16">
            <div className="flex flex-1 flex-col">
              {/* 현재는 details에 하나만 내려줌.  */}
              {nudgeData.details.map((detail) => {
                return (
                  <div>
                    <div className="flex flex-col gap-2">
                      {detail.nudgeType === 'STANDARD_MEMBERSHIP_DISCOUNT' ? (
                        <div className="flex items-center gap-4 typo-heading200">
                          <MembershipStandard28x14Icon />
                          <span className="typo-paragraph100-semibold">
                            멤버십쿠폰가
                          </span>
                          <ExpectedMembershipDiscountNoticeTooltip
                            nudgeTotalDiscountedPrice={detail.nudgeAmount}
                          />
                        </div>
                      ) : (
                        <div className="flex flex-wrap items-center gap-4 typo-heading200">
                          <span className="flex items-center gap-4 text-bonuspackGreen-text typo-paragraph200-semibold">
                            <BonuspackCloverFill18x18Icon />
                            행운 보너스팩
                          </span>
                          <span className="flex items-center gap-4 text-normal typo-paragraph200-semibold">
                            멤버십쿠폰가
                            <ExpectedBonuspackDiscountNoticeTooltip />
                          </span>
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <p className="flex items-center gap-8">
                          <strong className="text-error typo-heading200-bold">
                            {detail.nudgeAmount?.toLocaleString()}원
                          </strong>
                          <span className="text-hint typo-paragraph200-regular">
                            {data?.price?.toLocaleString()}원
                          </span>
                        </p>
                      </div>
                      {detail.descriptions.map((description) => {
                        return (
                          <p
                            className="flex items-center gap-4 break-all text-paragraph typo-paragraph100-regular"
                            key={description}
                          >
                            <ThreadIcon16x16 className="shrink-0 grow-0 basis-16" />
                            {description}
                          </p>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-center">
              <button
                type="button"
                onClick={onClickSubscribe}
                className="flex items-center justify-center rounded-12 bg-danger-regular px-12 py-4 text-gray-000 typo-paragraph100-semibold"
              >
                할인받기
              </button>
            </div>
          </div>
        </>
      )}
      {/* 미가입자 -> 스탠다드 경우만 노출 */}
      {!isAvailableToSubscribeBonusPack && (
        <MembershipTrialCountdown onClick={onClickSubscribe} />
      )}
    </div>
  );
}
